import React, { useRef, useLayoutEffect, useState, useEffect } from "react";

const CustomTextWidget = (props) => {
  const {
    id,
    options = {},
    value,
    required,
    disabled,
    readonly,
    autofocus,
    onChange,
    onBlur,
    onFocus,
    placeholder,
    schema,
  } = props;

  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const caretPosRef = useRef(0);
  const isFocusedRef = useRef(false);
  const pendingValueRef = useRef(null);

  const [internalValue, setInternalValue] = useState(typeof value === "undefined" || value === null ? "" : value);

  useEffect(() => {
    if (isFocusedRef.current) {
      return;
    }

    const normalized = typeof value === "undefined" || value === null ? "" : value;
    setInternalValue((prev) => (prev === normalized ? prev : normalized));
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceRef.current && pendingValueRef.current !== null) {
        onChange(pendingValueRef.current === "" ? options.emptyValue : pendingValueRef.current);
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [onChange, options.emptyValue]);

  useLayoutEffect(() => {
    if (isFocusedRef.current && inputRef.current && document.activeElement === inputRef.current) {
      const input = inputRef.current;
      const valueLength = String(internalValue ?? "").length;
      const pos = Math.min(caretPosRef.current, valueLength);

      try {
        input.setSelectionRange(pos, pos);
      } catch (e) { }
    }
  }, [internalValue]);

  const handleChange = (event) => {
    const val = event.target.value;
    const nextCaretPos = event.target.selectionStart ?? val.length;
    caretPosRef.current = nextCaretPos;
    pendingValueRef.current = val;

    setInternalValue(val);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      pendingValueRef.current = null;
      onChange(val === "" ? options.emptyValue : val);
    }, 500);
  };

  const handleBlur = (event) => {
    isFocusedRef.current = false;
    if (debounceRef.current && pendingValueRef.current !== null) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
      const latestValue = pendingValueRef.current;
      pendingValueRef.current = null;
      onChange(latestValue === "" ? options.emptyValue : latestValue);
    }
    if (onBlur) {
      onBlur(id, event.target.value);
    }
  };

  const handleFocus = (event) => {
    isFocusedRef.current = true;
    const currentPos = event.target.selectionStart || 0;
    caretPosRef.current = currentPos;
    if (onFocus) {
      onFocus(id, event.target.value);
    }
  };

  const inputType = options.inputType ? options.inputType : schema?.type === "number" ? "number" : "text";

  return (
    <input
      ref={inputRef}
      id={id}
      className="form-control"
      type={inputType}
      value={internalValue}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      readOnly={readonly}
      autoFocus={autofocus}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
    />
  );
};

export default CustomTextWidget;